// new code
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from 'react-router-dom'; // Import useLocation
import useToggleSidebar from "../../components/togglesidebar";
import SortIcon from "../../components/table/SortIcon";
import Sidebar from '../sidebar';
import Header from '../Header';
import Footer from '../footer';
import ChangePasswordModal from '../../components/Modal/ChangePasswordModal';
import '../../styles/challengerequests.css';
import api from "../../services/api";

let usersListCache = null;

function User() {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => usersListCache?.currentPage || 1);
  const [searchTerm, setSearchTerm] = useState(() => usersListCache?.searchTerm || "");
  const [itemsPerPage, setItemsPerPage] = useState(() => usersListCache?.itemsPerPage || 10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]); // This state is updated but not used for rendering the table. 'users' is used.
  const [successMessage, setSuccessMessage] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
  const [sortField, setSortField] = useState(() => usersListCache?.sortField || "createdAt");
  const [sortOrder, setSortOrder] = useState(() => usersListCache?.sortOrder || "desc");

  const initialScrollYRef = useRef(usersListCache?.scrollY || 0);
  const scrollRestoredRef = useRef(false);

  // No changes requested for this useEffect
  const location = useLocation(); // Keeping useLocation as it's part of the original code for alert handling
  useEffect(() => {
    if (location.state && location.state.message) {
      setSuccessMessage(location.state.message); // Assuming type is always 'success' for messages from location state
      // Clear the state after displaying the message to prevent it from showing again on refresh
      window.history.replaceState({}, document.title);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  }, [location.state]);

  useEffect(() => {
    usersListCache = {
      ...(usersListCache || {}),
      currentPage,
      itemsPerPage,
      searchTerm,
      sortField,
      sortOrder,
    };
  }, [currentPage, itemsPerPage, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    return () => {
      usersListCache = {
        ...(usersListCache || {}),
        scrollY: window.scrollY || 0,
      };
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const y = Number(initialScrollYRef.current || 0);
    if (y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading]);


  const fetchUsers = async () => {
    setLoading(true); // Start loading
    setError(null); // Clear previous errors
    try {
      const response = await api.get(`/users`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          sortField,
          sortOrder
        },
      });
      if (response.data.success && response.data.users) {
        setUsers(response.data.users || []);
        const total = response.data.total || response.data.users.length;
        const totalPages = response.data.totalPages || Math.ceil(total / itemsPerPage) || 1;
        setTotalPages(totalPages);
        setTotalUsers(total);
        setTableData(Array.isArray(response.data.users) ? response.data.users : []);
      } else {
        setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
        setTableData([]);
      }
    } catch (err) {
      if (err.response) {
        console.error("API Error:", err.response.data);
        setError(err.response?.data?.message || "Server Error");
      } else if (err.request) {
        console.error("Network Error:", err.message);
        setError("No internet connection or server is unreachable. Please check your network and try again.");
      } else {
        console.error("Error:", err.message);
        setError("An unexpected error occurred. Please try again.");
      }
      setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      fetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, sortField, sortOrder]);

  // This useEffect handles debouncing for the search term
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle role change
  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await api.patch(`/users/${userId}/role`, { role: newRole });
      if (response.data.success) {
        setSuccessMessage(response.data.message || "Role updated successfully");
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, role: newRole } : user
          )
        );
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error updating role:", error.response?.data?.message || error.message);
      setError(error.response?.data?.message || "Failed to update role");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (userId) => {
    try {
      const response = await api.patch(`/users/${userId}/active`, {});
      if (response.data.success) {
        setSuccessMessage(response.data.message || "Status updated successfully");
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, isActive: response.data.user.isActive } : user
          )
        );
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error toggling active status:", error.response?.data?.message || error.message);
      setError(error.response?.data?.message || "Failed to update status");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle open password modal
  const handleOpenPasswordModal = (userId, userEmail) => {
    setSelectedUser({ id: userId, email: userEmail });
    setPasswordModalOpen(true);
  };

  // Handle change password from modal
  const handleChangePassword = async (userId, newPassword) => {
    try {
      const response = await api.patch(`/users/${userId}/password`, { password: newPassword });
      if (response.data.success) {
        setSuccessMessage(response.data.message || "Password updated successfully");
        setTimeout(() => setSuccessMessage(""), 3000);
        setPasswordModalOpen(false);
        setSelectedUser(null);
      }
      return response;
    } catch (error) {
      console.error("Error updating password:", error.response?.data?.message || error.message);
      setError(error.response?.data?.message || "Failed to update password");
      setTimeout(() => setError(null), 3000);
      throw error;
    }
  };

  // Removed the early returns for loading and error to integrate them into the table body
  // if (loading) return <div></div>;
  // if (error) return <div>{error}</div>;

  const renderPaginationButtons = () => {
    const pages = [];
    const totalNumbersToShow = 5;
    const half = Math.floor(totalNumbersToShow / 2);

    let startPage = Math.max(currentPage - half, 1);
    let endPage = Math.min(currentPage + half, totalPages);

    if (currentPage <= half) {
      endPage = Math.min(totalNumbersToShow, totalPages);
    }

    if (currentPage > totalPages - half) {
      startPage = Math.max(totalPages - totalNumbersToShow + 1, 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key={1} className={`paginate_button ${currentPage === 1 ? "current active" : ""}`} onClick={() => setCurrentPage(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="start-ellipsis" className="ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`paginate_button ${currentPage === i ? "current active" : ""}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="end-ellipsis" className="ellipsis">...</span>);
      }
      pages.push(
        <button key={totalPages} className={`paginate_button ${currentPage === totalPages ? "current active" : ""}`} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Admin | User</title>
      </Helmet>
      <Sidebar isSidebarOpen={isSidebarOpen} />
      <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
        <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
          <Header toggleSidebar={toggleSidebar} />
          <section className='content'>
            <div className="breadcrumb-main-bx">
              <div className="breadcrumb-bx">
                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                <div className="breadcrumb-link breadcrumb-active">User</div>
              </div>
            </div>
            <div className='container-fluid'>

              {successMessage && (
                <div className="alert alert-success" role="alert">
                  {successMessage}
                </div>
              )}

              <div className='row'>
                <div className='col-md-12'>
                  <div className='card'>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>User</div>
                      <Link to="/admin/user/add" className="cbi-small common-btn-item cbi-fill text-decoration-none"><span><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"></path></svg>Add New User</span></Link>
                    </div>
                    <div className='card-body'>
                      <div id='challenge-requests-table_wrapper' className='dataTables_wrapper no-footer'>
                        <div className='dataTables_length' id='challenge-requests-table_length'>
                          <label>Show
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setCurrentPage(1);
                                setItemsPerPage(Number(e.target.value));
                              }}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                            entries
                          </label>
                        </div>
                        <div id='challenge-requests-table_filter' className='dataTables_filter'>
                          <label>Search:
                            <input
                              type="search"
                              className="form-control"
                              placeholder="Search..."
                              aria-controls="challenge-requests-table"
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                              }}
                            />
                          </label>
                        </div>
                        <div id='challenge-requests-table_processing' className='dataTables_processing'></div>
                        <div className='table-responsive'>
                          <table className="table table-bordered dataTable no-footer overflow-x-auto mt-2" id="challenge-requests-table">
                            <thead>
                              <tr role="row">
                                <th >No.</th>
                                <th
                                  className={`sorting_${sortField === "name" ? sortOrder : ""}`}
                                  onClick={() => {
                                    setSortField("name");
                                    setSortOrder(sortField === "name" && sortOrder === "asc" ? "desc" : "asc");
                                  }}
                                >
                                  User <SortIcon />
                                </th>
                                <th
                                  className={`sorting_${sortField === "email" ? sortOrder : ""}`}
                                  onClick={() => {
                                    setSortField("email");
                                    setSortOrder(sortField === "email" && sortOrder === "asc" ? "desc" : "asc");
                                  }}
                                >
                                  E-mail Id <SortIcon />
                                </th>
                                <th
                                  className={`sorting_${sortField === "createdAt" || sortField === "created_at" ? sortOrder : ""}`}
                                  onClick={() => {
                                    setSortField(sortField === "createdAt" || sortField === "created_at" ? "createdAt" : "createdAt");
                                    setSortOrder((sortField === "createdAt" || sortField === "created_at") && sortOrder === "asc" ? "desc" : "asc");
                                  }}
                                >
                                  Date<SortIcon />
                                </th>
                                <th>Role</th>
                                <th>Active</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? ( // Show skeleton only when loading
                                <tr>
                                  <td colSpan={8} style={{ textAlign: "center", padding: "10px" }}>
                                    <div className="row table-skeletant mx-0" style={{ display: 'block', width: '100%' }}>
                                      {/* Render itemsPerPage skeleton lines, default 5 if itemsPerPage is 0 */}
                                      {Array.from({ length: itemsPerPage > 0 ? itemsPerPage : 5 }).map((_, idx) => (
                                        <div className="col-12 px-0" key={idx}><div className="skeletant-bx skeletant-design sh-47"></div></div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ) : error ? ( // Show error if present and not loading
                                <tr>
                                  <td colSpan={8} style={{ textAlign: "center", padding: "10px", color: 'red' }}>
                                    Error: {error}
                                  </td>
                                </tr>
                              ) : users.length > 0 ? ( // If data exists (and not loading, no error), map and display data
                                users.map((row, index) => (
                                  <tr key={row._id || row.id || index}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td>{row.name || `${row.first_name || ""} ${row.last_name || ""}`.trim() || "N/A"}</td>
                                    <td>{row.email || "N/A"}</td>
                                    <td>
                                      {row.createdAt ? (() => {
                                        try {
                                          const date = new Date(row.createdAt);
                                          return isNaN(date.getTime()) ? "N/A" : date.toISOString().split('T')[0].split('-').reverse().join('-');
                                        } catch {
                                          return "N/A";
                                        }
                                      })() : row.created_at ? (() => {
                                        try {
                                          const date = new Date(row.created_at);
                                          return isNaN(date.getTime()) ? "N/A" : date.toISOString().split('T')[0].split('-').reverse().join('-');
                                        } catch {
                                          return "N/A";
                                        }
                                      })() : "N/A"}
                                    </td>
                                    <td>
                                      <select
                                        className="form-control form-control-sm"
                                        value={row.role || "admin"}
                                        onChange={(e) => handleRoleChange(row._id || row.id, e.target.value)}
                                        style={{ minWidth: "120px" }}
                                      >
                                        <option value="admin">Admin</option>
                                        <option value="superadmin">Super Admin</option>
                                      </select>
                                    </td>
                                    <td>
                                      <label className="switch">
                                        <input
                                          type="checkbox"
                                          checked={row.isActive !== false}
                                          onChange={() => handleToggleActive(row._id || row.id)}
                                        />
                                        <span className="slider round"></span>
                                      </label>
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleOpenPasswordModal(row._id || row.id, row.email)}
                                        title="Change Password"
                                      >
                                        Change Password
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : ( // If no data, not loading, and no error, show "No data available" message
                                <tr>
                                  <td colSpan={8} style={{ textAlign: "center", padding: "10px" }}>No data available in table</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          {/* The fixed skeleton div is removed from here */}
                        </div>
                        <div className="dataTables_info">
                          {totalUsers > 0 ? (
                            <>Showing {(itemsPerPage * (currentPage - 1)) + 1} to {Math.min(itemsPerPage * currentPage, totalUsers)} of {totalUsers} entries</>
                          ) : (
                            <>Showing 0 to 0 of 0 entries</>
                          )}
                        </div>
                        <div className="dataTables_paginate paging_simple_numbers">
                          <Link className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""}`} onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>Previous</Link>
                          <span>
                            {renderPaginationButtons()}
                          </span>
                          <Link className={`paginate_button next ${currentPage === totalPages ? "disabled" : ""}`} onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>Next</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* Your existing footer logic using tableData.length */}
          {tableData.length < 8 ? (
            <div className="ref-ft" style={{ marginLeft: isSidebarOpen ? "250px" : "0", }}>
              <Footer /> {/* Assuming Footer component is rendered here */}
            </div>
          ) : (
            <Footer />
          )}
        </div>
      </div>
      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => {
          setPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleChangePassword}
        userEmail={selectedUser?.email || ""}
        userId={selectedUser?.id || null}
      />
    </>
  )
}

export default User
